/**
 * Named copy slots for the customer app, with the strings that are hardcoded in JSX today as the
 * built-in defaults. Shape follows the existing precedent in
 * customer/src/utils/requestFormLabels.ts: an `as const` map plus a pure resolver.
 *
 * Resolution is three layers, highest first:
 *   1. the DB theme's `copy` (staff tweaks, no deploy - but arrives after first paint)
 *   2. the tenant's static `customer.copy` in packages/tenants (correct on first render)
 *   3. these defaults
 *
 * Copy is text content, so unlike styling it cannot ride a CSS variable and a late DB value will
 * visibly swap. A tenant's *permanent* wording therefore belongs in layer 2; layer 1 is for
 * tweaks and emergency fixes.
 */
export const CUSTOMER_COPY_DEFAULTS = {
  // Login / signup value-prop panel. Today this whole panel is gated to the Pelicargo tenant, so
  // every other tenant renders a blank column - these keys are what un-gate it.
  'auth.panel.heading': 'Experience the Pelicargo Difference',
  'auth.panel.subheading': "Use Pelicargo's intelligent sourcing technology to win more business.",
  'auth.panel.bullet1': 'Secure the best rates faster and easier than ever before',
  'auth.panel.bullet2': 'Gain access to powerful tools to navigate air cargo schedules & regulations',
  'auth.panel.bullet3': 'Ship any cargo type and size, with any carrier, anywhere!',

  // Onboarding signature step. NOTE: these defaults name Pelicargo and describe Pelicargo's own
  // contractual obligations. They are intentionally NOT interpolated with the tenant name - who
  // carries these obligations under a white label is a legal question, not a formatting one. A
  // tenant must supply its own legally-reviewed text via layer 2.
  'register.signature.heading': 'Welcome to Pelicargo',
  'register.signature.termsIntro': 'By signing below you accept the Pelicargo general',
  'register.signature.clause1':
    "1. Forwarder authorizes Pelicargo to solicit quotes and bookings on its behalf from Carriers for the air transportation of Forwarder's customers' cargo, and to communicate with Forwarder and Carrier personnel for that purpose. This authorization is specifically limited to the activities described in the foregoing sentence and may be revoked by Forwarder at any time upon written notice to Pelicargo or account cancelation.",
  'register.signature.clause2':
    '2. Forwarder acknowledges that, under the Pelicargo Terms of Service, Forwarder is responsible for providing accurate information about its air-transportation needs to Pelicargo, and that Pelicargo relies on the information regarding quotes for air transportation that it receives from Carriers. That said, Pelicargo is responsible for its own errors conveying information supplied by Forwarder to Carriers, or in conveying information supplied by Carriers to Forwarder.',
  'register.signature.clause3':
    '3. Forwarder acknowledges that, under the Pelicargo Terms of Service, Pelicargo will handle any competitively sensitive information it obtains in a manner consistent with U.S. antitrust laws.',
  'legal.privacyUrl': 'https://www.pelicargo.com/privacy',

  // Plain screen headings.
  'home.heading': 'Choose the best quotes',
  'requests.heading': 'Requests',
  'notFound.heading': "The page you are accessing doesn't exist.",

  // Migrated from TenantInfo['customer'].shipmentImporter, which this map replaces.
  'request.shipmentImporter.title': 'Shipment Importer',
  'request.shipmentImporter.placeholder': 'Paste your shipment details here to pre-fill with AI',
} as const satisfies Record<string, string>;

export type CustomerCopyKey = keyof typeof CUSTOMER_COPY_DEFAULTS;

export const CUSTOMER_COPY_KEYS = Object.keys(CUSTOMER_COPY_DEFAULTS) as CustomerCopyKey[];

export type CustomerCopyOverrides = Partial<Record<CustomerCopyKey, string>>;

/**
 * Resolves one slot through the three layers. `{name}` in a value is replaced with the tenant's
 * display name, so shared copy can be branded without duplicating it per tenant. The legal
 * clauses above deliberately contain no placeholder.
 */
export const resolveCustomerCopy = (
  key: CustomerCopyKey,
  layers: { db?: Record<string, string>; tenant?: CustomerCopyOverrides; tenantName?: string },
): string => {
  const raw = layers.db?.[key] ?? layers.tenant?.[key] ?? CUSTOMER_COPY_DEFAULTS[key];

  return layers.tenantName ? raw.replace(/\{name\}/g, layers.tenantName) : raw;
};
